import React, { useCallback, useEffect, useState } from 'react';
import _ from 'lodash';

import { makeStyles } from 'design-system/styles';
import { Alert, Button, H3, Option, Select, TextInput } from 'react-scale/components/design-system';
import Dialog, { DialogContent, DialogTitle } from 'react-scale/components/design-system/Dialog';

enum SelectionStrategy {
  'MostRecent' = 'Most Recent',
  'LeastRecent' = 'Least Recent',
  'Random' = 'Random',
}

interface SpotterAuditModalProps {
  attemptIds: string[];
  setAuditAttemptIds: (ids: string[]) => void;
  isOpen: boolean;
  onRequestClose: () => void;
}

const SpotterAuditModal = ({
  attemptIds,
  setAuditAttemptIds,
  isOpen,
  onRequestClose,
}: SpotterAuditModalProps) => {
  const [numberToAudit, setNumberToAudit] = useState<number>(0);
  const [selectionStrategy, setSelectionStrategy] = useState<SelectionStrategy>(
    SelectionStrategy.MostRecent,
  );

  useEffect(() => {
    if (isOpen) {
      setNumberToAudit(attemptIds.length >= 10 ? 10 : attemptIds.length);
    }
  }, [isOpen, attemptIds, setNumberToAudit]);

  const setNumber = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      try {
        const parsedNumber = parseInt(event.target.value || '0', 10);
        setNumberToAudit(parsedNumber > attemptIds.length ? attemptIds.length : parsedNumber);
      } catch (error) {
        setNumberToAudit(0);
      }
    },
    [setNumberToAudit, attemptIds],
  );
  const onButtonClick = useCallback(() => {
    if (selectionStrategy === SelectionStrategy.MostRecent) {
      const mostRecentAttempts = attemptIds.slice(-numberToAudit);
      setAuditAttemptIds(mostRecentAttempts);
    } else if (selectionStrategy === SelectionStrategy.LeastRecent) {
      const leastRecentAttempts = attemptIds.slice(numberToAudit - 1);
      setAuditAttemptIds(leastRecentAttempts);
    } else {
      const randomAttempts = _.sampleSize(attemptIds, numberToAudit);
      setAuditAttemptIds(randomAttempts);
    }
  }, [attemptIds, selectionStrategy, numberToAudit, setAuditAttemptIds]);

  const styles = useStyles();
  return (
    <Dialog open={isOpen} onClose={onRequestClose}>
      <DialogTitle>
        <div className={styles['spotter-audit-modal__title-container']}>
          There are {attemptIds.length} attempts available to audit
        </div>
      </DialogTitle>
      <DialogContent>
        <div>
          <Alert>We recommend auditing 5-15 attempts per spotter</Alert>
        </div>
        <div className={styles['spotter-audit-modal__body-container']}>
          <div className={styles['spotter-audit-modal__body-item']}>
            <H3>Audit</H3>
          </div>
          <div className={styles['spotter-audit-modal__body-item']}>
            <TextInput value={numberToAudit} onChange={setNumber} />
          </div>
          <div className={styles['spotter-audit-modal__body-item']}>
            <Select
              value={selectionStrategy}
              onChange={e => setSelectionStrategy(e.target.value as SelectionStrategy)}
            >
              {Object.entries(SelectionStrategy).map(([strategy, value]) => (
                <Option key={strategy} value={value}>
                  {value}
                </Option>
              ))}
            </Select>
          </div>
          <H3>attempts</H3>
        </div>
        <div className={styles['spotter-audit-tool__button-container']}>
          <Button type="primary" onClick={onButtonClick}>
            Start Audit
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const useStyles = makeStyles({
  'spotter-audit-modal__title-container': {
    display: 'flex',
    justifyContent: 'center',
  },
  'spotter-audit-modal__body-container': {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '32px 0 32px',
  },
  'spotter-audit-modal__body-item': {
    marginRight: '8px',
  },
  'spotter-audit-tool__button-container': {
    marginTop: '8px',
    display: 'flex',
    justifyContent: 'center',
  },
});

export default React.memo(SpotterAuditModal);
